import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Kanban, type ColumnaKanban } from '@xiltrion/ui/bloques/kanban';
import { usePedir } from '@xiltrion/ui/bloques/pedir';
import { Button } from '@xiltrion/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@xiltrion/ui/dropdown-menu';
import { Icono } from '@xiltrion/ui/icono';
import { avisar } from '@xiltrion/ui/sonner';
import { apiDelete, apiPatch, apiPost } from '@/api/client';
import { avisarError, keys, useLabels, useMoveTask, usePersonas, useTeams, useUpdateTask } from '@/api/hooks';
import type { Label, Section } from '@/api/types';
import type { ViewProps } from '@/app/extension-points';
import { useTerms } from '@/app/session';
import { TaskCard } from '../tasks/TaskCard';
import { QuickAdd } from '../tasks/QuickAdd';
import { applyFilters, groupTasks, patchForGroup } from '../tasks/view-logic';

/** Kanban (spec §3.1): columnas por sección o por cualquier campo de selección única; arrastrar cambia ese campo. */
export function BoardView({ project, tasks, view, onOpenTask }: ViewProps) {
  const qc = useQueryClient();
  const { porId: people } = usePersonas();
  const { teams } = useTeams();
  const { t: term } = useTerms();
  const teamNames = useMemo(() => new Map(teams.map((x) => [x.id, x.name])), [teams]);
  const { data: globalLabels = [] } = useLabels();
  const labels = useMemo(() => new Map<string, Label>([...globalLabels, ...(project?.labels ?? [])].map((l) => [l.id, l])), [globalLabels, project?.labels]);
  const groupBy = view?.config.groupBy || (project ? 'section' : 'due');
  const visible = applyFilters(tasks, view?.config);
  const groups = groupTasks(visible, groupBy, { sections: project?.sections ?? [], people, labels: [...labels.values()], teams: { names: teamNames, none: `Sin ${term('team').toLowerCase()}` } });
  const sectionMode = groupBy === 'section' && !!project;
  const canEdit = project ? project.capabilities.editTasks : true;
  const canManage = !!project?.capabilities.manage;
  const move = useMoveTask();
  const update = useUpdateTask();
  const refreshProject = () => project && void qc.invalidateQueries({ queryKey: keys.project(project.id) });

  const columnas: ColumnaKanban[] = groups.map((g) => {
    const section = project?.sections.find((s) => s.id === g.id);
    return {
      id: g.id,
      titulo: g.title || 'Grupo',
      color: g.color,
      limiteWip: sectionMode ? section?.wipLimit ?? null : null,
      colapsada: sectionMode ? section?.collapsed : false,
      acciones: sectionMode && canManage && section ? <SectionMenu section={section} onChanged={refreshProject} /> : undefined,
    };
  });
  const tarjetas = Object.fromEntries(groups.map((g) => [g.id, g.tasks]));

  return (
    <div className="flex h-full flex-col">
      <Kanban
        columnas={columnas}
        tarjetas={tarjetas}
        soloLectura={!canEdit}
        textoTarjeta={(t) => `la tarea ${t.title}`}
        contarWip={(ts) => ts.filter((t) => !t.completedAt).length}
        bloquearWip={!!project?.enforceWip}
        renderTarjeta={(t) => <TaskCard task={t} labels={labels} people={people} teamName={t.teamId ? teamNames.get(t.teamId) : undefined} onOpen={() => onOpenTask(t.id)} onToggle={canEdit ? () => update.mutate({ id: t.id, patch: { completed: !t.completedAt } }) : undefined} />}
        onMoverTarjeta={(m) => {
          if (sectionMode) move.mutate({ id: m.id, move: { sectionId: m.aColumna === '__none' ? null : m.aColumna, afterId: m.despuesDe, beforeId: m.despuesDe ? null : m.antesDe }, optimistic: { sectionId: m.aColumna } });
          else if (m.deColumna !== m.aColumna) {
            const patch = patchForGroup(groupBy, m.aColumna);
            if (patch) update.mutate({ id: m.id, patch });
          }
        }}
        onMoverColumna={sectionMode && canManage ? (m) => void apiPost(`/sections/${m.id}/move`, { afterId: m.despuesDe, beforeId: m.despuesDe ? null : m.antesDe }).then(refreshProject, avisarError) : undefined}
        onAlternarColumna={sectionMode ? (id, colapsada) => void apiPatch(`/sections/${id}`, { collapsed: colapsada }).then(refreshProject, avisarError) : undefined}
        pieColumna={(col) => (project && canEdit && (sectionMode || patchForGroup(groupBy, col.id)) ? <QuickAdd projectId={project.id} sectionId={sectionMode && col.id !== '__none' ? col.id : null} patch={sectionMode ? undefined : patchForGroup(groupBy, col.id) ?? undefined} /> : null)}
        className="flex-1 px-4 pt-3"
      />
    </div>
  );
}

function SectionMenu({ section, onChanged }: { section: Section; onChanged: () => void }) {
  const { pedir, dialogo } = usePedir();
  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="size-6" aria-label={`Opciones de ${section.name}`}><Icono nombre="mdi:dots-horizontal" /></Button></DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={async () => { const name = await pedir({ titulo: 'Renombrar la sección', etiqueta: 'Nombre', valorInicial: section.name, textoConfirmar: 'Renombrar' }); if (name) void apiPatch(`/sections/${section.id}`, { name }).then(onChanged, avisarError); }}><Icono nombre="mdi:pencil-outline" />Renombrar</DropdownMenuItem>
        <DropdownMenuItem onSelect={async () => { const v = await pedir({ titulo: 'Límite de tareas en curso', etiqueta: 'Límite', ayuda: 'Vacío = sin límite.', tipo: 'numero', valorInicial: section.wipLimit ? String(section.wipLimit) : '', requerido: false, validar: (x) => (x && !(Number(x) > 0) ? 'Escribe un número mayor que cero.' : null) }); if (v === null) return; const n = v ? Number(v) : null; void apiPatch(`/sections/${section.id}`, { wipLimit: n && n > 0 ? n : null }).then(onChanged, avisarError); }}><Icono nombre="mdi:timer-outline" />Límite WIP</DropdownMenuItem>
        <DropdownMenuItem variant="destructive" onSelect={() => void apiDelete(`/sections/${section.id}`).then(() => { avisar.success('Sección borrada'); onChanged(); }, avisarError)}><Icono nombre="mdi:trash-can-outline" />Borrar sección</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    {dialogo}
    </>
  );
}
